const DEFAULT_COUNTRY_CODE = '90';

/** Countries whose national form carries a trunk `0` in front of the subscriber number. */
export const TRUNK_ZERO_COUNTRY_CODES = ['90', '44', '49'];

export function normalizePhoneNumberToE164(raw: string | null | undefined): string | null {
  const trimmed = raw?.trim() ?? '';
  if (!trimmed || !containsOnlySupportedCharacters(trimmed)) {
    return null;
  }

  let compact = removeFormatting(trimmed);
  if (!compact) {
    return null;
  }

  if (compact.startsWith('00')) {
    compact = `+${compact.slice(2)}`;
  }

  if (compact.startsWith('+')) {
    return normalizeInternationalDigits(compact.slice(1));
  }

  if (!/^\d+$/.test(compact)) {
    return null;
  }

  if (compact.startsWith(DEFAULT_COUNTRY_CODE) && compact.length === 12) {
    return normalizeInternationalDigits(compact);
  }

  if (compact.length === 10) {
    return normalizeInternationalDigits(`${DEFAULT_COUNTRY_CODE}${compact}`);
  }

  if (compact.length === 11 && compact.startsWith('0')) {
    return normalizeInternationalDigits(`${DEFAULT_COUNTRY_CODE}${compact.slice(1)}`);
  }

  return null;
}

export function formatPhoneNumberToE123(e164: string | null | undefined): string {
  const value = e164?.trim() ?? '';
  if (!value) {
    return '';
  }

  const digits = value.startsWith('+') ? value.slice(1) : value;
  if (digits.length < 7) {
    return value;
  }

  if (digits.startsWith('90') && digits.length === 12) {
    return `+90 ${digits.slice(2, 5)} ${digits.slice(5, 8)} ${digits.slice(8, 10)} ${digits.slice(10, 12)}`;
  }

  if (digits.startsWith('44') && digits.length >= 12) {
    return `+44 ${digits.slice(2, 4)} ${digits.slice(4, 8)} ${digits.slice(8)}`;
  }

  if (digits.startsWith('1') && digits.length === 11) {
    return `+1 ${digits.slice(1, 4)} ${digits.slice(4, 7)} ${digits.slice(7, 11)}`;
  }

  if (digits.startsWith('49') && digits.length >= 11) {
    return `+49 ${digits.slice(2, 4)} ${digits.slice(4, 8)} ${digits.slice(8)}`;
  }

  const countryCodeLength = digits.length > 10 ? (digits[0] === '1' ? 1 : digits.length > 11 ? 3 : 2) : 1;
  const countryCode = digits.slice(0, countryCodeLength);
  const rest = digits.slice(countryCodeLength);

  if (!rest) {
    return `+${countryCode}`;
  }

  if (rest.length <= 4) {
    return `+${countryCode} ${rest}`;
  }

  return `+${countryCode} ${formatNationalDigitsInGroupsOfThree(rest)}`;
}

function normalizeInternationalDigits(digits: string): string | null {
  if (!/^\d+$/.test(digits)) {
    return null;
  }

  const normalizedDigits = removeTrunkZeroAfterCountryCode(digits);
  if (normalizedDigits.length < 8 || normalizedDigits.length > 15 || normalizedDigits.startsWith('0')) {
    return null;
  }

  return `+${normalizedDigits}`;
}

function removeTrunkZeroAfterCountryCode(digits: string): string {
  for (const countryCode of TRUNK_ZERO_COUNTRY_CODES) {
    if (!digits.startsWith(countryCode)) {
      continue;
    }

    const nationalStart = countryCode.length;
    if (digits.length > nationalStart && digits[nationalStart] === '0') {
      return `${digits.slice(0, nationalStart)}${digits.slice(nationalStart + 1)}`;
    }

    return digits;
  }

  return digits;
}

function removeFormatting(raw: string): string {
  let compact = '';
  for (const current of raw) {
    if (/\d/.test(current) || (current === '+' && compact.length === 0)) {
      compact += current;
    }
  }

  return compact;
}

function containsOnlySupportedCharacters(raw: string): boolean {
  for (const current of raw) {
    if (/\d/.test(current) || /\s/.test(current)) {
      continue;
    }

    if (current === '+' || current === '-' || current === '(' || current === ')' || current === '.') {
      continue;
    }

    return false;
  }

  return true;
}

function formatNationalDigitsInGroupsOfThree(nationalDigits: string): string {
  if (nationalDigits.length <= 4) {
    return nationalDigits;
  }

  const parts: string[] = [];
  for (let index = 0; index < nationalDigits.length; index += 3) {
    parts.push(nationalDigits.slice(index, index + 3));
  }

  return parts.join(' ');
}